
import React, { useContext } from "react";
import { Box, ButtonBase, Grid, Typography } from "@mui/material";
import { ImageURL } from "../../constants";
import { AddToCart, AddToWishlist } from "../index";
import useScrollAnimationProducts from "../../hooks/ScrollAnimationProducts";
import { AuthContext } from "../../AuthContext";
import { useTranslation } from "react-i18next";
import defaultImage from "../../assets/contactsvg.svg";
import { getName } from "../../utils";

const ProductGrid = React.memo(({ sortedProducts, navigate }) => {
  const { i18n } = useTranslation();
  const { currency, exchangeRate } = useContext(AuthContext);

  const [refs, getClass] = useScrollAnimationProducts(
    sortedProducts.length,
    "opacity-10 translate-y-10",
    "opacity-100 translate-y-0 transition-all duration-700 ease-out"
  );

  return (
    <Box
      className="border-2 border-gray-300 p-1 rounded-xl"
      sx={{
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      <Grid
        container
        spacing={2}
        justifyContent="flex-start"
        alignItems="stretch"
        sx={{
          width: "100%",
          margin: 0,
        }}
      >
        {sortedProducts.map((prod, i) => (
          <Grid
            item
            xs={6}
            sm={4}
            md={3}
            key={prod.id || i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            className={getClass(i)}
            sx={{
              display: "flex",
              minWidth: 0,
              boxSizing: "border-box",
            }}
          >
            <ButtonBase
              onClick={() => {
                const stored =
                  JSON.parse(
                    localStorage.getItem("recentProducts")
                  ) || [];

                const productData = {
                  id: prod.id,
                  name: prod.name,
                  image:
                    prod.images?.length > 0
                      ? ImageURL + prod.images[0]
                      : defaultImage,
                  price: prod.discount_price || prod.price,
                  slug: prod.slug,
                  code: prod.item_code,
                };

                const updated = [
                  productData,
                  ...stored.filter(
                    (item) => item.id !== prod.id
                  ),
                ].slice(0, 7);

                localStorage.setItem(
                  "recentProducts",
                  JSON.stringify(updated)
                );

                navigate("/product/" + prod.slug);
              }}
              sx={{
                display: "flex",
                width: "100%",
                minWidth: 0,
                height: "100%",
                textAlign: "left",
                alignItems: "stretch",
                justifyContent: "stretch",
                borderRadius: "8px",
              }}
            >
              <Box
                className="group bg-white border border-gray-200 p-3 hover:shadow-md transition-all duration-300 flex flex-col w-full h-full min-w-0"
                sx={{
                  width: "100%",
                  minWidth: 0,
                  boxSizing: "border-box",
                  overflow: "hidden",
                }}
              >
                {/* PRODUCT IMAGE */}
                <Box
                  className="relative w-full aspect-square rounded-md overflow-hidden flex items-center justify-center"
                  sx={{
                    flexShrink: 0,
                    backgroundColor: "#fff",
                  }}
                >
                  <img
                    loading="lazy"
                    src={
                      prod.images?.length > 0
                        ? ImageURL + prod.images[0]
                        : defaultImage
                    }
                    alt={prod.name}
                    className={`absolute inset-0 w-full h-full object-contain 
                      transition-all duration-700 ease-in-out
                      ${
                        prod.images?.length > 1
                          ? "scale-100 opacity-100 group-hover:scale-105 group-hover:opacity-0"
                          : "scale-100 group-hover:scale-105"
                      }`}
                  />

                  {/* SECOND IMAGE ON HOVER */}
                  {prod.images?.length > 1 && (
                    <img
                      loading="lazy"
                      src={ImageURL + prod.images[1]}
                      alt={`${prod.name} alternate view`}
                      className="absolute inset-0 w-full h-full object-contain 
                        scale-95 opacity-0
                        transition-all duration-700 ease-in-out
                        group-hover:scale-100 group-hover:opacity-100"
                    />
                  )}

                  {/* LEFT BADGES */}
                  <Box className="absolute top-2 left-2 flex flex-col gap-1">
                    {prod.is_new && (
                      <span className="bg-gray-200 text-xs font-semibold px-2 py-1 rounded">
                        NEW
                      </span>
                    )}

                    {prod.hot && (
                      <span className="bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded">
                        HOT
                      </span>
                    )}
                  </Box>

                  {/* ADD TO CART */}
                  <Box
                    className="absolute bottom-3 left-1/2 -translate-x-1/2
                      flex flex-col items-center gap-1
                      opacity-0 translate-y-6
                      group-hover:opacity-100 group-hover:translate-y-0
                      transition-all duration-300 ease-out"
                    sx={{
                      maxWidth: "95%",
                    }}
                  >
                    <Box onClick={(e) => e.stopPropagation()}>
                      <AddToCart
                        product={prod}
                        quantity={1}
                        className="poppins text-xs
                          w-48 max-w-full h-11
                          flex items-center justify-center
                          bg-[#1E55AC] text-white
                          rounded-full font-semibold shadow-md
                          hover:bg-[#02AFF3]
                          transition-all duration-200"
                      />
                    </Box>
                  </Box>

                  {/* RIGHT BADGES */}
                  <Box
                    className="absolute top-2 right-2
                      flex flex-col items-end gap-1
                      opacity-0 translate-x-6
                      group-hover:opacity-100 group-hover:translate-x-0
                      transition-all duration-300 ease-out"
                  >
                    {/* WISHLIST */}
                    <Box
                      onClick={(e) => e.stopPropagation()}
                      className="bg-white rounded-full shadow"
                    >
                      <AddToWishlist
                        product={prod}
                        products={[]}
                        setProducts={() => {}}
                        viaCategory={true}
                        open={false}
                        setOpen={() => {}}
                      />
                    </Box>

                    {/* DISCOUNT */}
                    {prod.discount_price && (
                      <span className="bg-green-500 text-white text-xs px-2 py-1 rounded">
                        -
                        {Math.round(
                          ((prod.price - prod.discount_price) /
                            prod.price) *
                            100
                        )}
                        %
                      </span>
                    )}
                  </Box>
                </Box>

                {/* PRODUCT DETAILS */}
                <Box
                  className="pt-3 flex flex-col flex-grow min-w-0"
                  sx={{
                    overflowWrap: "anywhere",
                  }}
                >
                  {/* STOCK */}
                  <Box className="flex items-center gap-1 text-gray-700 min-w-0">
                    <Typography
                      sx={{
                        fontSize: { xs: 12, sm: 13, md: 14 },
                        fontWeight: 700,
                        color:
                          Number(prod.stocks) < 20
                            ? "red"
                            : "green",
                      }}
                    >
                      {prod.stocks}
                    </Typography>

                    <Typography
                      className="text-gray-500 poppins"
                      sx={{
                        fontSize: { xs: 11, sm: 12, md: 14 },
                      }}
                    >
                      Stocks
                    </Typography>
                  </Box>

                  {/* ITEM CODE */}
                  <Typography
                    className="text-gray-400 poppins mt-1"
                    sx={{
                      fontSize: { xs: 10, sm: 11, md: 13 },
                      lineHeight: 1.5,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {prod.item_code}
                  </Typography>

                  {/* PRODUCT NAME */}
                  <Typography
                    className="text-[#2858A3] mt-1 poppins line-clamp-2 font-semibold"
                    sx={{
                      fontSize: { xs: 11, sm: 12, md: 14 },
                      lineHeight: 1.5,
                      minHeight: {
                        xs: "33px",
                        sm: "36px",
                        md: "42px",
                      },
                      overflowWrap: "anywhere",
                      cursor: "pointer",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {getName(prod, i18n.language)}
                  </Typography>

                  {/* PRICES */}
                  <Box className="flex flex-wrap items-baseline gap-x-2 gap-y-0 mt-auto pt-2 min-w-0">
                    <Typography
                      className="font-semibold poppins"
                      sx={{
                        fontSize: { xs: 12, sm: 14, md: 17 },
                        overflowWrap: "anywhere",
                      }}
                    >
                      {currency}{" "}
                      {Math.round(
                        (prod.discount_price || prod.price) *
                          exchangeRate
                      )}
                    </Typography>

                    {prod.discount_price && (
                      <Typography
                        className="text-gray-400 line-through"
                        sx={{
                          fontSize: {
                            xs: 10,
                            sm: 12,
                            md: 15,
                          },
                          overflowWrap: "anywhere",
                        }}
                      >
                        {currency}{" "}
                        {Math.round(prod.price * exchangeRate)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </ButtonBase>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
});

export default ProductGrid;
